import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, MusicalNoteIcon, ChatBubbleLeftRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getSetLists, getSetList, updateSetList } from '../services/setlistApi';

const AddToSetlistModal = ({ song, songs, onClose }) => {
  const [setlists, setSetlists] = useState([]);
  const [selectedSetlistId, setSelectedSetlistId] = useState('');
  const [setlistData, setSetlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSetlist, setLoadingSetlist] = useState(false);
  const [adding, setAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadSetlists();
  }, []);

  useEffect(() => {
    if (selectedSetlistId) {
      loadSetlistData(selectedSetlistId);
    } else {
      setSetlistData(null);
    }
  }, [selectedSetlistId]);

  const loadSetlists = async () => {
    try {
      setLoading(true);
      const data = await getSetLists();
      setSetlists(data);
      if (data.length > 0) {
        setSelectedSetlistId(data[0].id);
      }
    } catch (error) {
      console.error('Error loading setlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSetlistData = async (id) => {
    try {
      setLoadingSetlist(true);
      const data = await getSetList(id);
      setSetlistData(data);
    } catch (error) {
      console.error('Error loading setlist:', error);
    } finally {
      setLoadingSetlist(false);
    }
  };

  const handleInsert = async (setIndex, position) => {
    if (!setlistData || adding) return;

    try {
      setAdding(true);

      const newItem = {
        type: 'song',
        song_id: song.id,
        banter: null
      };

      const updatedSets = setlistData.sets.map((set, idx) => {
        if (idx === setIndex) {
          const newItems = [...set.items];
          newItems.splice(position, 0, newItem);
          return { ...set, items: newItems };
        }
        return set;
      });

      const updatedSetlistData = {
        name: setlistData.name,
        date: setlistData.date,
        sets: updatedSets
      };

      await updateSetList(selectedSetlistId, updatedSetlistData);

      // Refresh setlist data
      await loadSetlistData(selectedSetlistId);

      const setName = setlistData.sets[setIndex].name;
      setSuccessMessage(`Added "${song.title}" to ${setName}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding song to setlist:', error);
    } finally {
      setAdding(false);
    }
  };

  const getSongById = (songId) => {
    return songs.find(s => s.id === songId);
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getSetDuration = (set) => {
    let totalSeconds = 0;
    set.items.forEach(item => {
      if (item.type === 'song' && item.song_id) {
        const s = getSongById(item.song_id);
        if (s && s.duration) {
          totalSeconds += s.duration;
        }
      } else if (item.type === 'banter' && item.banter?.duration) {
        totalSeconds += item.banter.duration;
      }
    });
    return totalSeconds;
  };

  const InsertZone = ({ setIndex, position, label }) => (
    <button
      onClick={() => handleInsert(setIndex, position)}
      disabled={adding}
      className="w-full py-1 px-3 text-xs text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded border border-dashed border-transparent hover:border-purple-300 transition-colors flex items-center justify-center space-x-1"
    >
      <PlusIcon className="w-3 h-3" />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Add "{song.title}" to Setlist
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading setlists...</div>
          ) : setlists.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No setlists found. Create a setlist first.
            </div>
          ) : (
            <>
              {/* Setlist Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Setlist
                </label>
                <select
                  value={selectedSetlistId}
                  onChange={(e) => setSelectedSetlistId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                >
                  {setlists.map(setlist => (
                    <option key={setlist.id} value={setlist.id}>
                      {setlist.name} - {new Date(setlist.date).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-md text-green-800 flex items-center space-x-2">
                  <CheckIcon className="w-5 h-5" />
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Sets Display */}
              {loadingSetlist ? (
                <div className="text-center py-8 text-gray-500">Loading setlist...</div>
              ) : setlistData && setlistData.sets ? (
                <div className="space-y-4">
                  {setlistData.sets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      This setlist has no sets. Edit the setlist to add sets first.
                    </div>
                  ) : (
                    setlistData.sets.map((set, setIndex) => (
                      <div key={setIndex} className="border border-gray-200 rounded-lg p-4">
                        {/* Set Header */}
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">
                            {set.name}
                          </h4>
                          <span className="text-sm text-gray-500">
                            {set.items.length} {set.items.length === 1 ? 'item' : 'items'}, {formatDuration(getSetDuration(set))}
                          </span>
                        </div>

                        {/* Items with Insert Zones */}
                        <div className="space-y-1">
                          {/* Insert at beginning */}
                          <InsertZone
                            setIndex={setIndex}
                            position={0}
                            label={set.items.length === 0 ? "Add here" : "Add at beginning"}
                          />

                          {set.items.map((item, itemIndex) => (
                            <React.Fragment key={itemIndex}>
                              {/* Item Display */}
                              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center space-x-2">
                                  {item.type === 'song' ? (
                                    <>
                                      <MusicalNoteIcon className="w-4 h-4 text-blue-600" />
                                      <span className="text-sm">
                                        "{getSongById(item.song_id)?.title || 'Unknown'}" - {getSongById(item.song_id)?.artist || 'Unknown'}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <ChatBubbleLeftRightIcon className="w-4 h-4 text-purple-600" />
                                      <span className="text-sm">{item.banter?.name || 'Banter'}</span>
                                    </>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {item.type === 'song'
                                    ? formatDuration(getSongById(item.song_id)?.duration)
                                    : formatDuration(item.banter?.duration)}
                                </span>
                              </div>

                              {/* Insert after this item */}
                              <InsertZone
                                setIndex={setIndex}
                                position={itemIndex + 1}
                                label="Add here"
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToSetlistModal;
